import {lazy, memo, Suspense, useRef} from 'react'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {XIcon} from '@primer/octicons-react'
import {IconButton, Spinner} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {Panel} from './Panel'
import {getPanelHeading} from './panel-registry'
import {useHeaderPageData} from '../../page-data/loaders/use-header-page-data'
import {
  DockedPanel,
  useDockedPanelActions,
  useDockedPanelFocusRefs,
  useIsDockedPanelCompact,
  useIsDockedPanelOpen,
} from '../../contexts/DockedPanelContext'

const PullRequestsSidePanel = lazy(async () => {
  const m = await import('@github-ui/agent-sessions/components/task-view/PullRequestsSidePanel')
  return {default: m.PullRequestsSidePanel}
})

function AgentSessionsPanelBody() {
  const {
    data: {repository, pullRequest},
  } = useHeaderPageData()

  return (
    <ErrorBoundary
      boundaryName="AgentSessionsPanelBody"
      fallback={<div className="fgColor-muted text-small tmp-p-3">Unable to load agent sessions.</div>}
    >
      <Suspense
        fallback={
          <div className="d-flex flex-justify-center flex-items-center tmp-p-4">
            <Spinner size="medium" />
          </div>
        }
      >
        <PullRequestsSidePanel repository={repository} pullRequestNumber={pullRequest.number} />
      </Suspense>
    </ErrorBoundary>
  )
}

/**
 * Renders the agent sessions content inside the docked side panel, falling
 * back to a Dialog sidesheet in compact mode.
 */
export const AgentSessionsSidePanel = memo(function AgentSessionsSidePanelImpl() {
  const isOpen = useIsDockedPanelOpen(DockedPanel.AgentSessions)
  const isCompact = useIsDockedPanelCompact()
  const {closePanel} = useDockedPanelActions()
  const {returnFocusRef} = useDockedPanelFocusRefs()
  const closeButtonRef = useRef<HTMLButtonElement>(null)

  if (!isOpen) return null

  if (!isCompact) {
    return (
      <Panel panelName={DockedPanel.AgentSessions}>
        <Panel.Body className="p-0">
          <Panel.ViewportFill>
            <AgentSessionsPanelBody />
          </Panel.ViewportFill>
        </Panel.Body>
      </Panel>
    )
  }

  return (
    <Dialog
      initialFocusRef={closeButtonRef}
      position={{narrow: 'fullscreen', regular: 'right', wide: 'right'}}
      returnFocusRef={returnFocusRef}
      onClose={closePanel}
      height="large"
      renderBody={({children: bodyChildren}) => <Dialog.Body className="p-0">{bodyChildren}</Dialog.Body>}
      renderHeader={({dialogLabelId}) => (
        <Dialog.Header>
          <div className="d-flex flex-row flex-justify-between flex-items-center width-full pl-2">
            <Dialog.Title id={dialogLabelId}>{getPanelHeading(DockedPanel.AgentSessions)}</Dialog.Title>
            <IconButton
              ref={closeButtonRef}
              aria-label="Close agent sessions panel"
              icon={XIcon}
              variant="invisible"
              onClick={closePanel}
            />
          </div>
        </Dialog.Header>
      )}
    >
      <Panel.DialogFill>
        <AgentSessionsPanelBody />
      </Panel.DialogFill>
    </Dialog>
  )
})
